import GroupLabel from '@/components/UI/GroupLabel/GroupLabel';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { calculateAge, capitalise, formatNumber } from '@/helpers';
import { _isEmpty, _isNil, _isNotEmpty, _isNumber, _notNil } from '@/littledash';
import type { ID } from '@/model/Common.model';
import type { TreatmentGroup } from '@/model/TreatmentGroup.model';
import { TbInfoCircle } from 'react-icons/tb';
import type { RandomAnimal, RandomTreatmentGroup } from '../Steps/Randomize/Randomize.model';
import type { OneWayAnovaResults } from '../Steps/Randomize/Statistics.model';
import { RandomizationAttributes } from './Report.model';

export type CriteriaOptions = Record<string, { name: string; value: string }>;

export const generateAnimalColumns = (
  animals: Array<RandomAnimal>,
  studyGroups: Array<RandomTreatmentGroup>,
  studyId: ID,
  studyApiId: string,
  criteriaOptions: CriteriaOptions
): Array<TanStackColumnDef<RandomAnimal>> => {
  const measurementColumns = Object.keys(animals?.[0].measurement_info ?? {}).map<TanStackColumnDef<RandomAnimal>>(
    (key) => ({
      id: key,
      header: criteriaOptions[key].name,
      accessorFn: (row: RandomAnimal) => row.measurement_info?.[key],
      isVisible: true,
      cell: ({ row: { original } }) =>
        _notNil(original.measurement_info?.[key]) ? formatNumber(original.measurement_info?.[key]) : '-',
    })
  );

  const altIdColumns = animals.reduce<Record<string, TanStackColumnDef<RandomAnimal>>>((acc, animal, idx, array) => {
    if (
      Object.prototype.hasOwnProperty.call(acc, 'donor') &&
      Object.prototype.hasOwnProperty.call(acc, 'ear') &&
      Object.prototype.hasOwnProperty.call(acc, 'tag') &&
      Object.prototype.hasOwnProperty.call(acc, 'tail')
    ) {
      return acc;
    }
    return (['donor', 'ear', 'tag', 'tail'] as Array<keyof RandomAnimal>).reduce((innerAcc, key) => {
      if (!Object.prototype.hasOwnProperty.call(innerAcc, key) && _isNotEmpty(animal[key])) {
        innerAcc[key] = {
          id: `alt_id.${key}`,
          accessorKey: key,
          header: key.charAt(0).toUpperCase() + key.slice(1),
          isVisible: true,
        };
      }
      return innerAcc;
    }, acc);
  }, {});
  return [
    {
      id: 'name',
      header: 'Name',
      accessorKey: 'name',
      isVisible: true,
    },
    ...Object.values(altIdColumns),
    {
      id: 'cage',
      header: 'Cage at randomization',
      accessorKey: 'cage_name',
      isVisible: true,
    },
    {
      id: 'sex',
      header: 'Sex',
      accessorKey: 'sex',
      isVisible: true,
      cell: ({
        row: {
          original: { sex },
        },
      }) => capitalise(sex),
    },
    {
      id: 'dob',
      header: 'D.O.B',
      accessorKey: 'dob',
      isVisible: true,
      cell: ({
        row: {
          original: { dob },
        },
      }) => (_notNil(dob) ? calculateAge(dob) : '-'),
    },
    {
      id: 'study_group',
      header: 'Group Assigned',
      accessorFn: (row: RandomAnimal) => row.group,
      isVisible: true,
      size: 200,
      cell: ({
        row: {
          original: { group },
        },
      }) => (_notNil(group) ? <GroupLabel group={group as unknown as TreatmentGroup} /> : 'Excluded'),
    },
    ...measurementColumns,
  ];
};

const measurementTypes: Record<string, string> = {
  mean: 'Mean',
  sem: 'SEM',
  sd: 'STD DEV',
  median: 'Median',
  mad: 'MAD',
};

const attrTypes: Record<RandomizationAttributes, string> = {
  [RandomizationAttributes.sex]: 'Sex',
  [RandomizationAttributes.donor_id]: 'Donor ID',
  [RandomizationAttributes.dob]: 'Date of birth',
};

export const resultColumns = (
  studyGroups: Array<RandomTreatmentGroup>,
  criteriaOptions: CriteriaOptions,
  errorDeviation: string,
  oneWayAnova: Record<string, OneWayAnovaResults>,
  {
    openModal,
    closeModal,
  }: {
    openModal: (modal: any, props: any) => void;
    closeModal: () => void;
  }
): Array<TanStackColumnDef<RandomTreatmentGroup>> => {
  const measurementColumns: Array<TanStackColumnDef<RandomTreatmentGroup>> = Object.entries(
    studyGroups[0].measurements
  ).map(([measure, values]) => {
    const columnName = `${studyGroups[0].name.replace(' ', '')}.${measure}`;
    const pValue = oneWayAnova?.[criteriaOptions[measure].value]?.pvalue;
    return {
      id: columnName,
      header: () => (
        <div>
          <div>{criteriaOptions[measure].name}</div>
          {_isNotEmpty(oneWayAnova) && (
            <div className="flex flex-row dark-gray normal f6 pointer">
              {_notNil(pValue) && _isNumber(pValue) ? `P = ${pValue?.toFixed(4)}` : 'No P Value'}
              {
                <a
                  className="flex items-center ml1"
                  onClick={() => {
                    openModal('ONEWAYANOVA_TABLE', {
                      oneWayAnova: oneWayAnova?.[criteriaOptions[measure].value],
                      closeModal: closeModal,
                    });
                  }}
                >
                  <TbInfoCircle />
                </a>
              }
            </div>
          )}
        </div>
      ),
      columns: Object.keys(values).map<TanStackColumnDef<RandomTreatmentGroup>>((key) => ({
        id: `${columnName}.${key}`,
        header:
          key === 'mean' || key === 'median'
            ? measurementTypes[key]
            : measurementTypes[key === 'mad' ? 'mad' : errorDeviation],
        accessorFn: (row: RandomTreatmentGroup) => row.measurements?.[measure]?.[key],
        isVisible: true,
        cell: ({ row: { original } }) => formatNumber(original.measurements?.[measure]?.[key]),
      })),
    } as TanStackColumnDef<RandomTreatmentGroup>;
  });

  const subjectsColumns = (): Array<TanStackColumnDef<RandomTreatmentGroup>> => {
    const subjectType: string | null = Object.keys(studyGroups?.[0]?.subjects ?? {})?.[0] ?? null;

    if (studyGroups.every(({ subjects }) => _isEmpty(subjects)) || _isNil(subjectType)) {
      return [];
    } else {
      const attributes = studyGroups.flatMap((group) => {
        return Object.keys(group.subjects?.[subjectType]);
      });
      // In TanStack Table v8, column groups are defined using the 'columns' property
      return [
        {
          id: `subjects.${subjectType}`,
          header: attrTypes[subjectType as RandomizationAttributes],
          columns: Array.from(new Set(attributes)).map((key) => ({
            id: `subjects.${subjectType}.${key}`,
            header: String(
              subjectType === RandomizationAttributes.dob
                ? `${key} (${calculateAge(key, new Date().toISOString(), true)})`
                : capitalise(key)
            ),
            accessorFn: (row: RandomTreatmentGroup) => row.subjects?.[subjectType]?.[key],
            isVisible: true,
          })),
        } as TanStackColumnDef<RandomTreatmentGroup>,
      ];
    }
  };
  return [
    {
      id: 'study_group',
      header: 'Group',
      accessorKey: 'api_id',
      isVisible: true,
      size: 200,
      cell: ({ row: { original } }) => <GroupLabel group={original as unknown as TreatmentGroup} />,
    },
    {
      id: 'total_animals',
      header: 'Total animals',
      accessorKey: 'total_animals',
      isVisible: true,
    },
    ...subjectsColumns(),
    ...measurementColumns,
  ];
};
